import type { PixelEvent } from '@/events';

import {
  BaseDestinationAdapter,
  DEFAULT_COMMERCE_EVENT_TYPES,
} from './BaseDestinationAdapter';
import type { DestinationResult } from '../types';

export type ConsoleDestinationOptions = {
  readonly log?: (message: string, payload?: unknown) => void;
};

/**
 * Test / debug destination — logs events to console without side effects.
 */
export class ConsoleDestination extends BaseDestinationAdapter {
  private readonly log: (message: string, payload?: unknown) => void;

  private sent = 0;

  constructor(options: ConsoleDestinationOptions = {}) {
    super({
      name: 'console',
      version: '1.0.0',
      supportedTypes: DEFAULT_COMMERCE_EVENT_TYPES,
    });
    this.log =
      options.log ??
      ((message, payload) => {
        // eslint-disable-next-line no-console
        console.log(message, payload);
      });
  }

  async execute(context: {
    readonly event: PixelEvent;
    readonly signal: AbortSignal;
  }): Promise<DestinationResult> {
    const startedAt = Date.now();

    if (context.signal.aborted) {
      return this.createResult('failed', Date.now() - startedAt, {
        error: 'Aborted',
      });
    }

    const payload = {
      type: context.event.type,
      timestamp: context.event.timestamp,
      category:
        'category' in context.event ? context.event.category : undefined,
    };

    this.log(`[Console] ${context.event.type}`, payload);
    this.sent += 1;

    return this.createResult('success', Date.now() - startedAt, {
      metadata: { sent: this.sent },
    });
  }

  getSentCount(): number {
    return this.sent;
  }
}
